import { useEffect, useState } from 'react';

/**
 * Reactive online/offline status.
 *
 * navigator.onLine has known limitations:
 *   - Reliable for "definitely offline" (false = no network interface)
 *   - Unreliable for "definitely online" (true on broken networks too)
 *
 * For our use case (showing an overlay when the user disconnects), this
 * is good enough. The overlay disappears the moment the OS says we're
 * back online, and a real failed API call will reveal a captive portal
 * or broken network anyway.
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
